/**
 * Ширины колонок таблицы, заданные пользователем.
 *
 * Ширина колонки — свойство РАБОЧЕГО МЕСТА, а не лаборатории: на широком
 * мониторе владельца и на ноутбуке техника удобны разные раскладки, и общая
 * настройка означала бы, что один портит вид другому. Поэтому хранится
 * в браузере, рядом с масштабом таблицы.
 *
 * Пока пользователь ничего не трогал, ширин НЕТ вовсе, и таблица раскладывается
 * сама по содержимому. Первое же перетаскивание запоминает ширины ВСЕХ колонок
 * такими, какими они были на экране, и таблица переходит в жёсткую раскладку —
 * иначе от первой же правки прыгнули бы все остальные колонки.
 */

import { useCallback, useEffect, useState } from 'react';

export type ColumnWidths = Record<string, number>;

/** Уже колонки не бывает: в неё не помещается даже стрелка сортировки. */
const MIN_WIDTH = 48;

export function useColumnWidths(storageKey: string) {
  const [widths, setWidths] = useState<ColumnWidths | null>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      // Битое значение не должно ломать экран — просто вернёмся к авторазметке.
      const clean: ColumnWidths = {};
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof value === 'number' && Number.isFinite(value)) {
          clean[key] = Math.max(MIN_WIDTH, Math.round(value));
        }
      }
      return Object.keys(clean).length > 0 ? clean : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (widths) window.localStorage.setItem(storageKey, JSON.stringify(widths));
      else window.localStorage.removeItem(storageKey);
    } catch {
      // Приватный режим или переполненное хранилище: ширины просто не
      // переживут перезагрузку. Ронять из-за этого экран незачем.
    }
  }, [storageKey, widths]);

  /** Задать ширину одной колонки. `base` — снимок всех ширин на момент захвата. */
  const setWidth = useCallback((id: string, width: number, base?: ColumnWidths) => {
    setWidths((current) => ({
      ...(current ?? base ?? {}),
      [id]: Math.max(MIN_WIDTH, Math.round(width)),
    }));
  }, []);

  const reset = useCallback(() => setWidths(null), []);

  return { widths, setWidth, reset, custom: widths !== null };
}
